import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Row, Image } from 'react-bootstrap';

import { RootState } from '@/ui/store/store';
import { useAppSelector } from '@/ui/store/helperRedux';

import CartController from '@/controllers/CartController';

import { translate } from '@/domain/utils/translate';

import { ICart } from '@/domain/interfaces/ICart';
import { IRegistrationData, IIdentification } from '@/domain/interfaces/IUser';
import { IStore } from '@/domain/interfaces/IStore';


import MainTitle from '@/ui/components/MainTitle';
import PurchaseTotals from '@/ui/components/sales/PurchaseTotals';
import UserDataForm from '@/ui/components/user/UserDataForm';


/**propiedades de la interfaz de PaymentLayout*/
interface PropsPaymentLayout {
  /** Objeto tipo useRef que almacena la información de carrito */
  formRef: React.RefObject<ICart>;
  /** Función para actualizar el atributo formRef  */
  updateFormRef: (newFormRef: ICart) => void;
  /** Función para el botón del siguiente paso */
  handleNextButton: (e: MouseEvent) => void;
}

/**
 * Componente para formulario de pago y envío de productos
 * @component
 */
const PaymentLayout = ({ handleNextButton, formRef, updateFormRef }: PropsWithChildren<PropsPaymentLayout>): JSX.Element => {

  /** VARIABLES DE ENTORNO */
  const URL_IMAGES: string = process.env.REACT_APP_URL_IMAGES ?? '';

  /**Trae la información del carrito desde Redux */
  const cartProducts = useAppSelector((state: RootState) => state.cartState) as ICart;

  /**Trae la información de la tienda desde Redux */
  const storeData = useAppSelector((state: RootState) => state.storeData) as IStore;


  /** Establece el formulario a actualizar */
  const formCartCheckOut = useRef<ICart>(formRef.current
    ? JSON.parse(JSON.stringify(formRef.current))
    : JSON.parse(JSON.stringify(cartProducts)))

  /** Controlador de carrito */
  const { updateClientInCart } = CartController();

  /**Estado de la data para envío del formulario */
  const [validData, setValidData] = useState<boolean>(false);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  /** Cargar la identificación guardada en localStorage al iniciar */
  useEffect(() => {
    try {
      const savedIdentification = localStorage.getItem('checkout_identification');
      if (savedIdentification) {
        const identification: IIdentification = JSON.parse(savedIdentification);

        /** Actualizar el formulario con la identificación guardada */
        if (formCartCheckOut.current && identification.type) {
          formCartCheckOut.current.clientDetails.identificationId = identification;

          /** Actualizar también el formRef y el estado global */
          updateFormRef(JSON.parse(JSON.stringify(formCartCheckOut.current)));
          updateClientInCart(formCartCheckOut.current.clientDetails);
        }
      }
    } catch (error) {
      /** Si hay error al parsear, limpiar el localStorage para evitar errores futuros */
      localStorage.removeItem('checkout_identification');
      /** La validación del formulario se manejará normalmente sin la identificación guardada */
    }
  }, []);

  /**función para enviar la información del formulario al siguiente paso */
  const handleSendInfo = () => {
    if (validData && !isSubmitting) {
      setIsSubmitting(true);
      localStorage.removeItem('checkout_identification');
      handleNextButton(new MouseEvent('click'));
    }
  };


  const updateClientData = (clientData: IRegistrationData): void => {

    if (clientData.error) {
      setValidData(false);
    } else {
      setValidData(true);

      formCartCheckOut.current.clientDetails.firstName = clientData.firstName;
      formCartCheckOut.current.clientDetails.lastName = clientData.lastName;
      formCartCheckOut.current.clientDetails.identificationId = clientData.identificationId;
      formCartCheckOut.current.clientDetails.email = clientData.email;
      formCartCheckOut.current.clientDetails.phone = clientData.phone;

      updateFormRef(JSON.parse(JSON.stringify(formCartCheckOut.current)));
      updateClientInCart(formCartCheckOut.current.clientDetails);
    }
  }

  /**CONSTANTES DE TEXTO */
  const CLIENT_INFO_TITLE = translate('checkout.client_info_title');
  const CLIENT_INFO_DESCRIPTION = translate('checkout.client_info_description');
  const PAYMENT_METHODS_TITLE = translate('checkout.payment_methods_title');

  const WEBPAY_LOGO: string = `${URL_IMAGES}/static/webpay-payment-button.png`;

  /**Renderizado del componente */
  return (
    <Container fluid className='px-0 pb-2'>
      <Row className='g-0 my-2 '>
        <Col xs={12} lg={8} className='bg-primary-4 rounded rounded-3 shadow px-3'>
          <Col xs={12} lg={10} className='text-start mx-auto pt-4 '>
            <Row className='g-0'>
              <MainTitle
                titleSize='h2'
                iconName='ico-user'
                iconClass='svg-secondary-1 pe-2 svg-32'
                boldTitle={CLIENT_INFO_TITLE}
                detailsText={CLIENT_INFO_DESCRIPTION}
              />
            </Row>
            <UserDataForm
              clientData={formCartCheckOut.current.clientDetails}
              updateClientData={updateClientData}
              disabledForm={storeData.purchaseOnlyClient}
            />
          </Col>
        </Col>
        {/** Columna de resumen */}
        <Col xs={12} lg={4} className='ps-md-3 mt-3  mt-md-0 h-100 '>
          <Col lg={12} className='bg-primary-4 rounded rounded-3  shadow rounded py-4 pt-2 pe-2 px-2 d-flex flex-column pb-0 '>
            <Col className='d-flex pb-5 px-0'>
              <PurchaseTotals data={cartProducts} />
            </Col>
            <Col className='pb-3 d-flex flex-column'>
              <Col>
                <MainTitle boldTitle={PAYMENT_METHODS_TITLE} />
              </Col>
              <Col className='pb-0 my-2'>
                <Button
                  className={`btn btn btn-webpay-standard border text-blue w-100 py-0 py-0  ${!validData || isSubmitting ? 'disabled' : ''
                    }`}
                  type='button'
                  onClick={handleSendInfo}
                  disabled={!validData || isSubmitting}
                >
                  <Image src={WEBPAY_LOGO} alt='webpay' />
                </Button>

              </Col>
            </Col>
          </Col>
        </Col>
        {/** Fin columna de resumen */}
      </Row>
    </Container>
  );
};

export default PaymentLayout;
